import {ReactElement, useCallback, useEffect, useRef, useState} from "react";
import {UUID4, wineScannerApi} from "lib/api/wineScannerApi";
import {Spinner} from "components/UIComponents/Spinner";
import {getSelectedWineIds} from "./wineScannerUtils";
import {useAlgoliaProductIndex} from "features/algolia/algoliaHooks";
import {isReady} from "lib/Loadable";
import {AlgoliaWine} from "features/winepicker/winePickerModel";
import {WinePickerResultWine} from "features/winepicker/WinePickerResultWine";
import {Language} from "features/i18n/translations";
import { FormattedMessage } from "react-intl";

export interface WineScannerDetailProps {
    jobId: UUID4;
    language: Language;
    pickedWines: Record<string, AlgoliaWine>;
    onSetWinePicked: (wine: AlgoliaWine, selected: boolean) => void;
}

export function WineScannerDetail(props: WineScannerDetailProps): ReactElement {
    const {language, pickedWines, onSetWinePicked} = props;
    const algoliaIndex = useAlgoliaProductIndex();

    const [wineIds, setWineIds] = useState<string[] | null>(null);
    const [wines, setWines] = useState<AlgoliaWine[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        setWineIds(null);
        setWines(null);
        wineScannerApi.getScanJobResult(props.jobId)
            .then(response => setWineIds(getSelectedWineIds(response)))
            .catch(() => {
                setError('Fehler beim Laden des Scanergebnisses.');
                setLoading(false);
            });
    }, [props.jobId]);

    useEffect(() => {
        if (wineIds == null || !isReady(algoliaIndex)) return;
        algoliaIndex.loaded.getObjects<AlgoliaWine>(wineIds)
            .then(({results}) => {
                setWines(results.filter((w): w is AlgoliaWine => w != null));
                setLoading(false);
            })
            .catch(() => {
                setError('Fehler beim Laden der Weindaten.');
                setLoading(false);
            });
    }, [wineIds, algoliaIndex]);

    const setSelected = useCallback(
        (wine: AlgoliaWine, selected: boolean) => onSetWinePicked(wine, selected),
        [onSetWinePicked]
    );

    const selectedToggleRef = useRef(true);
    const selectAll = useCallback(()=>{
        wines?.forEach(w=>onSetWinePicked(w, selectedToggleRef.current));
        selectedToggleRef.current = !selectedToggleRef.current;
    }, [wines, onSetWinePicked]);

    if (loading) {
        return (
            <div className="flex-auto flex items-center justify-center">
                <Spinner className="w-10 h-10"/>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex-auto flex items-center justify-center">
                <span className="text-primary font-barlow">{error}</span>
            </div>
        );
    }

    return (
        <div className="flex-auto overflow-y-auto px-11 py-4">
            <button onClick={selectAll} className="text-primary font-bold mb-4 cursor-pointer">
                <FormattedMessage id="winepicker.favorites.selectAll"/>
            </button>
            <ul className="flex flex-col gap-1">
                {(wines ?? []).map(wine => (
                    <li key={wine.objectID}>
                        <WinePickerResultWine
                            wine={wine}
                            selected={wine.objectID in pickedWines}
                            onSetSelected={setSelected}
                            language={language}
                        />
                    </li>
                ))}
            </ul>
            {(wines ?? []).length === 0 && (
                <div className="text-light-text font-barlow text-center py-8">
                    Keine Weine gefunden.
                </div>
            )}
        </div>
    );
}
